import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Typography,
  Box,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import ApiService from '../../services/api-service';
import notifier from '../Notifier';
import { getErrorString, HMS, truncate } from '../../constants/function';
import { Images } from '../../assets/common';
import NoData from '../Nodata';

export default function SongRequestList({ open, onClose, roomId, onAcceptSong }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    if (open && roomId) {
      fetchRequests();
    }
  }, [open, roomId]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await ApiService.SONG_REQUEST_LIST(roomId);
      if (response.success) {
        setRequests(response.result || []);
      }
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (request) => {
    setProcessingId(request._id);
    try {
      const response = await ApiService.SONG_REQUEST_ACCEPT(request._id);
      if (response.success) {
        notifier.success('Song request accepted!');
        
        // Remove the accepted request from the list
        setRequests(requests.filter(r => r._id !== request._id));
        
        // Pass the song to the parent component to add to playlist
        if (onAcceptSong && response.result.song) {
          onAcceptSong(response.result.song);
        }
      }
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (request) => {
    setProcessingId(request._id);
    try {
      const response = await ApiService.SONG_REQUEST_REJECT(request._id);
      if (response.success) {
        notifier.success('Song request rejected and tip refunded');
        
        // Remove the rejected request from the list
        setRequests(requests.filter(r => r._id !== request._id));
      }
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setProcessingId(null);
    }
  };

  const handleClearProcessed = async () => {
    try {
      const response = await ApiService.SONG_REQUEST_CLEAR(roomId);
      if (response.success) {
        notifier.success('Cleared processed requests');
      }
    } catch (error) {
      notifier.error(getErrorString(error));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: '#1A1A1A',
          color: '#ffffff',
          borderRadius: 16,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <img 
              src={Images.jukebox} 
              alt="Song Requests" 
              style={{ width: 24, height: 24, filter: 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)' }}
            />
            <Typography variant="h6">Song Requests</Typography>
            {requests.length > 0 && (
              <Chip
                label={requests.length}
                size="small"
                sx={{
                  backgroundColor: '#4CAF50',
                  color: '#ffffff',
                  fontWeight: 600,
                }}
              />
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={fetchRequests} size="small" disabled={loading}>
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : requests.length === 0 ? (
          <Box py={4}>
            <NoData label="No song requests yet" />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ mt: 2 }}>
            {requests.map((request) => (
              <Box
                key={request._id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: '#2A2A2A',
                  border: '1px solid #333',
                  '&:hover': {
                    borderColor: '#444',
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  {/* Song Thumbnail */}
                  <img
                    src={request.songDetails?.thumbnailImg?.filePath || Images.default_music}
                    alt={request.songDetails?.title}
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: 8,
                      objectFit: 'cover',
                    }}
                  />

                  {/* Song Info */}
                  <Box flex={1}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {truncate(request.songDetails?.title, 40)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                      {request.songDetails?.artist} • {HMS(request.songDetails?.duration)}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                      <img
                        src={request.requesterId?.profileImg?.filePath || Images.default_user_square}
                        alt={request.requesterId?.firstName}
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                        }}
                      />
                      <Typography variant="caption" sx={{ color: '#b0b0b0' }}>
                        Requested by {request.requesterId?.firstName} {request.requesterId?.lastName}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Tip Amount */}
                  <Chip
                    label={`${request.tipAmount} SIKI`}
                    sx={{
                      backgroundColor: '#4CAF50',
                      color: '#ffffff',
                      fontWeight: 600,
                      fontSize: '14px',
                    }}
                  />

                  {/* Action Buttons */}
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      onClick={() => handleAccept(request)}
                      disabled={processingId === request._id}
                      sx={{
                        backgroundColor: '#4CAF50',
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: '#45a049',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#333',
                          color: '#666',
                        },
                      }}
                    >
                      {processingId === request._id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CheckIcon />
                      )}
                    </IconButton>
                    <IconButton
                      onClick={() => handleReject(request)}
                      disabled={processingId === request._id}
                      sx={{
                        backgroundColor: '#f44336',
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: '#d32f2f',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#333',
                          color: '#666',
                        },
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            ))}

            {/* Clear Processed Button */}
            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="center">
              <Button
                onClick={handleClearProcessed}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: '#666',
                  color: '#b0b0b0',
                  '&:hover': {
                    borderColor: '#888',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                Clear Processed Requests
              </Button>
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}